import { useEffect, useRef } from 'react'
import { readMiroQueue } from '@shared/services'

/** What was sent: the queue's send time and what was in it. */
export interface MiroSent {
  at: string
  items: string[]
}

export type MiroSendNote = { kind: 'ok' | 'err'; text: string } | null

/** How often to ask how it is going. */
const POLL_MS = 3000
/** After the panel has taken them, how long to wait for the server's fill to show up. */
const FILL_SHOWS_UP_MS = 15_000
/** Nobody opens Miro for the rest of the afternoon: stop asking. */
const GIVE_UP_MS = 30 * 60 * 1000

/**
 * Follow a selection sent to Miro until it is on the board.
 *
 * Sending clears the selection at once (2026-09-30), so the page's message is
 * all that is left of it -- and it should go when the pictures are on the
 * board, not linger. This asks the server every few seconds:
 *
 *   still in the queue          the message stays as it is
 *   taken, the server filling   "Placing on your Miro board… 40 of 100"
 *   all placed                  the message goes
 *   some not placed             says how many, and stays until dismissed
 *   taken, marked placed        one picture, placed by the panel itself
 *                               (no frame, no server): the message goes
 *   taken, no fill at all       a panel that never says, or Dismiss in the
 *                               panel: the message goes after a while
 *
 * `onNote(null)` means "clear the message". `onSettled` means there is
 * nothing more to follow.
 */
export function useMiroSendProgress(
  sent: MiroSent | null,
  onNote: (note: MiroSendNote) => void,
  onSettled: () => void,
): void {
  // Held in refs so a new callback on every render does not restart the watch.
  const note = useRef(onNote)
  const settled = useRef(onSettled)
  note.current = onNote
  settled.current = onSettled

  useEffect(() => {
    if (!sent) return
    let cancelled = false
    let over = false
    const started = Date.now()
    const sentAt = Date.parse(sent.at) || started
    const mine = new Set(sent.items)
    let takenAt = 0

    const finish = (last: MiroSendNote) => {
      over = true
      note.current(last)
      settled.current()
    }

    const check = async () => {
      if (cancelled || over) return
      if (Date.now() - started > GIVE_UP_MS) {
        finish(null)
        return
      }
      const queue = await readMiroQueue('web')
      if (cancelled || over || queue.failed) return
      // Still waiting for somebody to press Add to board.
      if (queue.items.some((item) => mine.has(item))) return
      takenAt ||= Date.now()

      const fill = queue.fill
      const ours = fill && (Date.parse(fill.createdAt) || 0) >= sentAt - 1000
      // Placed by the panel itself, with no fill to follow (one picture).
      const placed = (Date.parse(queue.placedAt) || 0) >= sentAt - 1000
      if ((!fill || !ours) && placed) {
        finish(null)
        return
      }
      if (!fill || !ours) {
        if (Date.now() - takenAt > FILL_SHOWS_UP_MS) finish(null)
        return
      }
      if (fill.status === 'queued' || fill.status === 'running') {
        note.current({ kind: 'ok', text: `Placing on your Miro board… ${fill.placed} of ${fill.total}` })
        return
      }
      if (fill.status === 'done' && fill.failed === 0) finish(null)
      else if (fill.status === 'done') {
        finish({ kind: 'err', text: `${fill.placed} placed on your Miro board; ${fill.failed} could not be. ${fill.error}` })
      } else {
        finish({ kind: 'err', text: `Not placed on your Miro board. ${fill.error}` })
      }
    }

    void check()
    const timer = setInterval(() => void check(), POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [sent])
}
